import { useState } from 'react'
import { ListItem, type ListItemData, type ListSide } from '../ListItem'

interface ProConListProps {
  side: ListSide
  items: ListItemData[]
  onAdd: (label: string) => void
  onEdit: (id: string, label: string) => void
  onDelete: (id: string) => void
  onToggleWeight: (id: string, currentWeight: number) => void
  isTouchDevice?: boolean
  autoFocus?: boolean
  addPlaceholder?: string
  proHeader?: string
  conHeader?: string
  editLabel?: string
  deleteLabel?: string
  markKeyPointLabel?: string
  unmarkKeyPointLabel?: string
}

export function ProConList({
  side,
  items,
  onAdd,
  onEdit,
  onDelete,
  onToggleWeight,
  isTouchDevice = false,
  autoFocus = false,
  addPlaceholder,
  proHeader = 'Pros',
  conHeader = 'Cons',
  editLabel,
  deleteLabel,
  markKeyPointLabel,
  unmarkKeyPointLabel,
}: ProConListProps) {
  const [newItem, setNewItem] = useState('')
  const isPro = side === 'PRO'
  const headerBg = isPro ? 'var(--zine-pro)' : 'var(--zine-con)'

  function handleSubmit(e: React.SyntheticEvent) {
    e.preventDefault()
    if (!newItem.trim()) return
    onAdd(newItem.trim())
    setNewItem('')
  }

  return (
    <div>
      <div
        className="flex items-center justify-between px-4 py-3 text-white text-xs font-bold tracking-widest uppercase"
        style={{
          backgroundColor: headerBg,
          borderBottom: '2px solid var(--zine-border)',
        }}
      >
        <span>{isPro ? proHeader : conHeader}</span>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex"
        style={{ borderBottom: '1px solid rgba(128,128,128,0.1)' }}
      >
        <input
          autoFocus={autoFocus}
          type="text"
          data-test={`${side.toLowerCase()}-reason-input`}
          className="flex-1 text-base md:text-sm px-3.5 py-2.5 bg-transparent border-none outline-none text-ink dark:text-dark-ink placeholder:text-[rgba(128,128,128,0.4)] dark:placeholder:text-[#908C87] transition-colors"
          placeholder={addPlaceholder ?? (isPro ? 'Add a pro…' : 'Add a con…')}
          value={newItem}
          onChange={(e) => setNewItem(e.currentTarget.value)}
        />
        <button
          aria-label={addPlaceholder ?? (isPro ? 'Add pro' : 'Add con')}
          type="submit"
          className="w-10 text-xl font-light text-ink-secondary dark:text-dark-ink2 bg-transparent border-none cursor-pointer transition-colors hover:text-white flex items-center justify-center shrink-0 dark:hover:text-white"
          style={{ borderLeft: '1px solid rgba(128,128,128,0.1)' }}
          onMouseEnter={(e) => {
            ;(e.currentTarget as HTMLButtonElement).style.backgroundColor =
              headerBg
          }}
          onMouseLeave={(e) => {
            ;(e.currentTarget as HTMLButtonElement).style.backgroundColor =
              'transparent'
          }}
        >
          +
        </button>
      </form>

      <ul
        className="pb-2 overflow-hidden"
        data-test={`${side.toLowerCase()}-reason-list`}
      >
        {items.map((item) => (
          <ListItem
            key={item.id}
            {...item}
            isTouchDevice={isTouchDevice}
            onEdit={onEdit}
            onDelete={onDelete}
            onToggleWeight={onToggleWeight}
            editLabel={editLabel}
            deleteLabel={deleteLabel}
            markKeyPointLabel={markKeyPointLabel}
            unmarkKeyPointLabel={unmarkKeyPointLabel}
          />
        ))}
      </ul>
    </div>
  )
}
